import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowLeft,
    CalendarDays,
    MapPin,
    Users,
    ClipboardCheck,
    CheckCircle2,
    Download,
    Lock,
    Notebook,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

function authedDownload(url, filename, label) {
    const token = (() => { try { return localStorage.getItem("cursus_physio_token"); } catch { return null; } })();
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then(async (r) => {
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                throw new Error(e.detail || "Téléchargement impossible");
            }
            return r.blob();
        })
        .then((blob) => {
            const u = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = u; a.download = filename; a.click();
            URL.revokeObjectURL(u);
            toast.success(`${label} téléchargé`);
        })
        .catch((err) => toast.error(err.message));
}

function downloadPdf(sessionId) {
    authedDownload(`${API}/sessions/${sessionId}/emargement.pdf`, `emargement-${sessionId}.pdf`, "Feuille d'émargement");
}

function downloadRoster(sessionId) {
    authedDownload(`${API}/formateur/sessions/${sessionId}/roster.pdf`, `liste-${sessionId}.pdf`, "Liste stagiaires");
}

export default function FormateurSessionDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(false);

    const refresh = () => {
        setLoading(true);
        api.get(`/formateur/sessions/${id}/emargements`)
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    };
    useEffect(refresh, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const allSigned = data?.rows.every((r) => r.emargement?.morning?.signed && r.emargement?.afternoon?.signed);
    const validated = data?.rows.some((r) => r.emargement?.formateur_validated);

    const validate = async () => {
        if (!allSigned) {
            const ok = window.confirm(
                "Certains stagiaires n'ont pas signé toutes les périodes. Valider quand même ?\n\n" +
                "Les absences seront tracées dans le PDF.",
            );
            if (!ok) return;
        }
        setValidating(true);
        try {
            await api.post(`/formateur/sessions/${id}/emargement/validate`, {});
            toast.success("Émargements validés");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setValidating(false);
        }
    };

    const pointage = async (userId, period) => {
        try {
            await api.post(`/formateur/sessions/${id}/emargements/pointage`, {
                user_id: userId,
                morning_present: period === "morning" || period === "both",
                afternoon_present: period === "afternoon" || period === "both",
            });
            toast.success("Pointage enregistré");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (loading || !data) {
        return <div className="mx-auto max-w-6xl px-6 py-16 text-center text-brand-smoke">Chargement…</div>;
    }

    const { session, rows } = data;

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-formateur-session-detail">
            <Link to="/formateur/sessions" className="inline-flex items-center gap-2 text-sm text-brand-smoke hover:text-brand-ink">
                <ArrowLeft className="h-4 w-4" /> Retour
            </Link>

            <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                {session.cursus_slug}
            </div>
            <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">{session.formation_name}</h1>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-smoke">
                <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" /> {formatDate(session.start_date)} · {session.start_time}–{session.end_time}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> {session.venue_name}, {session.city}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" /> {rows.length} stagiaires
                </span>
            </div>

            {/* Action bar */}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                        <ClipboardCheck className="mr-1 inline h-3.5 w-3.5" />
                        Émargements de la session
                    </div>
                    <div className="mt-1 font-display text-xl">
                        {validated ? "✓ Émargements validés" : allSigned ? "Tous signés — prêt à valider" : "En attente de signatures"}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => downloadPdf(session.id)}
                        data-testid="btn-download-emargement"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                    >
                        <Download className="h-3.5 w-3.5" /> Émargement PDF
                    </button>
                    <button
                        onClick={() => downloadRoster(session.id)}
                        data-testid="btn-download-roster"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                    >
                        <Download className="h-3.5 w-3.5" /> Liste papier
                    </button>
                    <Link
                        to={`/formateur/sessions/${session.id}/bilan`}
                        data-testid="btn-open-bilan"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                    >
                        <Notebook className="h-3.5 w-3.5" /> Bilan pédagogique
                    </Link>
                    <button
                        onClick={validate}
                        disabled={validating || validated}
                        data-testid="btn-validate-emargements"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                    >
                        {validated ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        {validated ? "Validés" : validating ? "Validation…" : "Valider les émargements"}
                    </button>
                </div>
            </div>

            {/* Roster */}
            <div className="mt-8 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-brand-line bg-[#FAFAFA] text-left text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                                <th className="px-6 py-4">Stagiaire</th>
                                <th className="px-6 py-4">Email</th>
                                <th className="px-6 py-4">ADELI/RPPS</th>
                                <th className="px-6 py-4 text-center">Matin</th>
                                <th className="px-6 py-4 text-center">Après-midi</th>
                                <th className="px-6 py-4 text-center">Pointage manuel</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-line">
                            {rows.length === 0 ? (
                                <tr><td colSpan={6} className="px-6 py-10 text-center text-brand-smoke">Aucun stagiaire inscrit.</td></tr>
                            ) : rows.map((r) => {
                                const u = r.user || {};
                                const em = r.emargement || {};
                                const m = em.morning?.signed;
                                const a = em.afternoon?.signed;
                                return (
                                    <tr key={u.id} data-testid={`emargement-row-${u.email}`}>
                                        <td className="px-6 py-4 font-medium text-brand-ink">{u.first_name} {u.last_name}</td>
                                        <td className="px-6 py-4 text-brand-smoke">{u.email}</td>
                                        <td className="px-6 py-4 text-brand-smoke">{u.adeli_rpps || "—"}</td>
                                        <td className="px-6 py-4 text-center">
                                            {m ? (
                                                <img src={em.morning.signature_data} alt="signature matin" className="mx-auto h-8" />
                                            ) : <span className="text-red-500">✗</span>}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {a ? (
                                                <img src={em.afternoon.signature_data} alt="signature après-midi" className="mx-auto h-8" />
                                            ) : <span className="text-red-500">✗</span>}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {!m && (
                                                <button onClick={() => pointage(u.id, "morning")} className="text-xs text-brand-orange hover:underline mr-2">
                                                    + matin
                                                </button>
                                            )}
                                            {!a && (
                                                <button onClick={() => pointage(u.id, "afternoon")} className="text-xs text-brand-orange hover:underline">
                                                    + après-midi
                                                </button>
                                            )}
                                            {m && a && <span className="text-xs text-green-700">✓ Complet</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
